import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import mixpanel from 'mixpanel-browser';
import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import {
  addToCart,
  updateSMSNumberConfirmed,
} from '../../../../actions/v3Order';
import cartUtils from '../../../../utils/cart';
import {
  isProductAllowed,
  isShippingRestrictedForParent,
} from '../../helpers/internationalRestriction';
import BackLinkFromProduct from '../../shared/BackLinkFromProduct.jsx';
import RequiredProductForm from '../required-product-form/RequiredProductForm.jsx';
import {
  DoneWrapper,
  ImageContainer,
  ResponsiveImage,
  ZoomIcon,
  ZoomImageContainer,
} from './regularStyles';
import { pickNextConfigSlot } from './regularProductConfigSlots';
import { ZoomButtonWrapper } from '../../productConfig/productConfigStyles';
import LegacyRequiredProductForm from '../legacy-required-product-form/LegacyRequiredProductForm.jsx';
import AddToCartButton from './subcomponents/AddToCartButton';
import BackgroundsPosesSelector from './subcomponents/BackgroundsPosesSelector';
import DeclineInterstitialProductuButton from './subcomponents/DeclineInterstitialProductuButton';
import DeclinePreCartOfferProductuButton from './subcomponents/DeclinePreCartOfferProductuButton';
import PersonalizationForm from './subcomponents/PersonalizationForm';
import ProductHeader from './subcomponents/ProductHeader';
import ProductSubheader from './subcomponents/ProductSubheader';
import CropOptionsPrepaySelector from './subcomponents/CropOptionsPrepaySelector';
import ProductOptionsSelector, {
  getProductOptionsDefaultsKey,
  reconcileProductOptions,
} from './subcomponents/ProductOptionsSelector';
import SMSNumberConfirmation from './subcomponents/SMSNumberConfirmation';
import SelectedPosesAndBackgrounds from './subcomponents/SelectedPosesAndBackgrounds';

const ProductOptionsWrapper = styled.div`
  margin: 20px 0;
`;

const RegularProductConfig = ({
  product,
  hideBackLink,
  incentiveProducts,
  interstitialProducts,
  productIsPreCartOffer,
  redirectSource,
}) => {
  const [backgroundSelections, setBackgroundSelections] = useState([]);
  const [backgroundsPosesSelectorVisible, setBackgroundsPosesSelectorVisible] =
    useState(false);
  const [personalizationValue, setPersonalizationValue] = useState('');
  const [isIncentiveProduct, setIsIncentiveProduct] = useState(false);
  const [isInterstitialProduct, setIsInterstitialProduct] = useState(false);
  const [isPreCartOfferProduct, setIsPreCartOfferProduct] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [personalizationOffered, setPersonalizationOffered] = useState(false);
  const [poseSelections, setPoseSelections] = useState([]);
  const [poseSelectionIds, setPoseSelectionIds] = useState([]);
  const [activeConfigSlotIndex, setActiveConfigSlotIndex] = useState(null);
  const [activeConfigStep, setActiveConfigStep] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [shouldOfferPersonalization, setShouldOfferPersonalization] =
    useState(true);
  const [selectedProductOptionKey, setSelectedProductOptionKey] =
    useState(null);
  const [selectedProductOptions, setSelectedProductOptions] = useState({});
  const [
    productOptionsDefaultsInitializedFor,
    setProductOptionsDefaultsInitializedFor,
  ] = useState(null);
  const [cropSelections, setCropSelections] = useState([]);
  const [isZoomed, setIsZoomed] = useState(false);
  const { control, handleSubmit, register, setValue } = useForm();

  const {
    allAvailablePosesForOrder,
    availablePoses,
    studentGallery,
    selectedStudent,
    isGeskus,
    picsGalleryId,
    studentGroupImages,
    studentId,
    studentPreviewImageUrl,
    enableZoomOnProducts,
  } = useSelector((state) => state.additionalData);
  const orders = useSelector((state) => state.v3Order.orders);
  const currentUserId = useSelector((state) => state.auth.currentUserId);
  const { phone, smsPhone } = useSelector((state) => state.parent);
  const { chargeForYpxBackgroundUpgrades } = useSelector(
    (state) => state.shoot,
  );
  const isPrepay = useSelector((state) => state.shoot.isPrepay);
  const isGreenScreen = useSelector((state) => state.shoot.isGreenScreen);
  const cropOptions = useSelector((state) => state.shoot?.crop_options || []);
  const cropOptionsEnabled = useSelector(
    (state) => state.shoot?.crop_options_enabled || false,
  );
  const backgroundOptions = useSelector(
    (state) => state.shoot.backgroundOptions,
  );
  const personalizationMaxLength = useSelector(
    (state) => state.shoot.personalizationMaxLength || 20,
  );
  const prepayPersonalizationOfferType = useSelector(
    (state) => state.shoot.prepayPersonalizationOfferType,
  );
  const smsNumberConfirmed = useSelector(
    (state) => state.v3Order.smsNumberConfirmed,
  );
  const parent = useSelector((state) => state.parent);
  const shoot = useSelector((state) => state.shoot.shoot);
  const shippingRestricted = isShippingRestrictedForParent(shoot, parent);

  const studentFirstName =
    selectedStudent?.firstName || selectedStudent?.first_name || null;
  const studentLastName =
    selectedStudent?.lastName || selectedStudent?.last_name || null;
  const hasStudent = studentFirstName && studentLastName;

  // console.log('==========================================');
  // console.log('Product for config:', product);
  // console.log('isPrepay:', isPrepay);
  // console.log('==========================================');

  const backgroundOptionsCount = product.background_options_count;
  const posesCount = product.poses_count;
  const productCropOptionsEnabled = product.crop_options_enabled !== false;
  const showCropOptions =
    isPrepay &&
    cropOptionsEnabled &&
    productCropOptionsEnabled &&
    posesCount > 0 &&
    cropOptions.length > 0;
  const isGroupImage = product.image_type === 'group_image';
  const studentGroupImage = (studentGroupImages && studentGroupImages[0]) || {};

  const orderContainsSingleBackgroundUpgrade =
    cartUtils.orderContainsSingleBackgroundUpgrade(
      orders,
      studentId,
      chargeForYpxBackgroundUpgrades,
    );

  const resetConfig = useCallback(() => {
    setBackgroundSelections([]);
    setPoseSelections([]);
    setPoseSelectionIds([]);
    setActiveConfigSlotIndex(null);
    setActiveConfigStep(null);
    setCropSelections([]);
    setBackgroundsPosesSelectorVisible(false);
  }, []);

  // TODO: is there a better way of doing this?
  useEffect(() => {
    if (
      isPrepay &&
      prepayPersonalizationOfferType ===
        'prepay_personalization_prompted_before_checkout_only'
    ) {
      setShouldOfferPersonalization(false);
    } else {
      setShouldOfferPersonalization(true);
    }

    setPersonalizationOffered(false);
    setPersonalizationValue('');
    resetConfig();

    if (
      incentiveProducts &&
      incentiveProducts
        .map((incentiveProduct) => incentiveProduct.id)
        .includes(product.id)
    ) {
      setIsIncentiveProduct(true);
      setBackgroundsPosesSelectorVisible(true);
      Swal.fire(
        'Congratulations',
        'You qualify for a free bonus item. Please add your bonus item to your cart. You may need to select a background.',
      );
    }
  }, [product, isPrepay, prepayPersonalizationOfferType, resetConfig]);

  useEffect(() => {
    if (
      interstitialProducts &&
      interstitialProducts
        .map((interstitialProduct) => interstitialProduct.id)
        .includes(product.id)
    ) {
      setIsInterstitialProduct(true);
      Swal.fire(
        'Recommended Product',
        'Based on your order so far, here is a product we think you might like 😊',
      );
    }
  }, [product]);

  useEffect(() => {
    if (productIsPreCartOffer) {
      setIsPreCartOfferProduct(true);
      Swal.fire(
        'Is Your Cart Missing Something?',
        'Here is a product we think you might like!',
      );
    }
  }, [product]);

  // Check if this is a digital download that skips background selection
  const skipBackgroundSelection =
    product?.is_digital_download &&
    (product?.digital_download_background_type === 'no_background' ||
      product?.digital_download_background_type === 'all_backgrounds');

  /* on desktop, we want to show the backgrounds selector, which is otherwise hidden by default */
  // TODO: review how we're handling the conditional rendering logic
  useEffect(() => {
    // For digital downloads that skip background selection, show selector if poses are needed (but not in prepay)
    if (
      !isPrepay &&
      skipBackgroundSelection &&
      posesCount > 0 &&
      (availablePoses?.length > 0 ||
        studentGallery?.length > 0 ||
        allAvailablePosesForOrder?.length > 0)
    ) {
      setBackgroundsPosesSelectorVisible(true);
    }
    // Original logic for non-digital downloads on desktop
    else if (
      windowWidth > 991 &&
      (backgroundOptionsCount > 0 || posesCount > 0) &&
      (backgroundOptions.length > 0 || availablePoses?.length > 0) &&
      !skipBackgroundSelection
    ) {
      setBackgroundsPosesSelectorVisible(true);
    }
    // For mobile, also show selector when needed (but not in prepay)
    else if (
      !isPrepay &&
      windowWidth <= 991 &&
      skipBackgroundSelection &&
      posesCount > 0 &&
      (availablePoses?.length > 0 || studentGallery?.length > 0)
    ) {
      // Don't auto-show on mobile, let user toggle it, but make sure it's available
      // The toggle function will handle showing/hiding
    }
  }, [
    product,
    windowWidth,
    backgroundOptionsCount,
    posesCount,
    backgroundOptions.length,
    availablePoses?.length,
    studentGallery?.length,
    allAvailablePosesForOrder?.length,
    skipBackgroundSelection,
    isPrepay,
  ]); // This effect runs whenever these dependencies change

  // Initialize default product options
  useEffect(() => {
    const productOptionsDefaultsKey = getProductOptionsDefaultsKey(product);
    if (
      !productOptionsDefaultsKey ||
      productOptionsDefaultsInitializedFor === productOptionsDefaultsKey
    ) {
      return;
    }

    setSelectedProductOptions((currentOptions) =>
      reconcileProductOptions(product, currentOptions),
    );
    setProductOptionsDefaultsInitializedFor(productOptionsDefaultsKey);
  }, [product, productOptionsDefaultsInitializedFor]); // Run when product changes

  const dispatch = useDispatch();

  const handleZoomChange = useCallback((shouldZoom) => {
    setIsZoomed(shouldZoom);
  }, []);

  const handleZoomClick = (e) => {
    e.stopPropagation();
    setIsZoomed((zoomed) => !zoomed);
  };

  const productBackgroundOptionsCount = product?.background_options_count || 0;
  const smsNumber = smsPhone;
  const productRequiresSMS = product.sms_required;
  const personalizationRequired =
    product.personalization_status === 'personalization_required';
  const personalizationOptional =
    product.personalization_status === 'personalization_optional';

  const openPosesBackgroundsSelector = (slotIndex = null, step = null) => {
    if (slotIndex !== null && slotIndex !== undefined && step) {
      setActiveConfigSlotIndex(slotIndex);
      setActiveConfigStep(step);
      setBackgroundsPosesSelectorVisible(true);
      return;
    }

    // No explicit target (mobile toggle / first open): pick the next empty
    // slot across both pose & background steps so we don't land on an
    // already-filled pose when only a background slot is empty.
    const next = pickNextConfigSlot({
      posesCount,
      productBackgroundOptionsCount,
      poseSelections,
      backgroundSelections,
      skipBackgroundSelection,
      backgroundOptions,
    });

    if (next) {
      setActiveConfigSlotIndex(next.slotIndex);
      setActiveConfigStep(next.step);
      setBackgroundsPosesSelectorVisible(true);
      return;
    }

    // Everything is filled — user is reopening to review/edit. Default to
    // slot 0 of whichever step exists for this product.
    setActiveConfigSlotIndex(0);
    setActiveConfigStep(posesCount > 0 ? 'pose' : 'background');
    setBackgroundsPosesSelectorVisible(true);
  };

  // toggles the visibility of the backgrounds and poses selector on mobile devices
  const togglePosesBackgroundsSelector = () => {
    if (!backgroundsPosesSelectorVisible) {
      openPosesBackgroundsSelector();
      return;
    }

    setBackgroundsPosesSelectorVisible(false);
  };

  const handleConfirmSMS = async () => {
    const phoneNumberObject = parsePhoneNumberFromString(
      smsPhone || phone,
      'US',
    );

    if (!phoneNumberObject || !phoneNumberObject.isValid()) {
      Swal.fire('Invalid Phone Number', '', 'error');
      return;
    }
    const result = await Swal.fire({
      title: 'Please Confirm',
      text: `Please confirm ${phoneNumberObject.formatNational()} is the mobile number where we should send your SMS-based products`,
      confirmButtonText: 'OK',
      showCancelButton: true,
      reverseButtons: true,
      focusConfirm: true,
      cancelButtonText: 'Edit',
    });

    if (result.value === true) {
      dispatch(updateSMSNumberConfirmed(true));
      Swal.fire(
        'Confirmed',
        'You may now add the product to your cart',
        'success',
      );
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      setEditMode(true);
    }
  };

  const addProductToCart = () => {
    handleSubmit((formState) => {
      if (!smsNumberConfirmed && productRequiresSMS) {
        handleConfirmSMS();
      } else {
        const backgroundIds = backgroundSelections.map(
          (background) => background.id,
        );

        if (productIsPreCartOffer) {
          mixpanel.track('Pre-Cart Offer Button Click', {
            Selection: 'Add to Cart',
          });
        }
        dispatch(
          addToCart({
            studentId: selectedStudent.id,
            fullproductData: product,
            backgroundSelections,
            backgroundIds,
            poseIds: poseSelectionIds,
            poses: poseSelections,
            isInterstitialProduct,
            productIsPreCartOffer: isPreCartOfferProduct,
            quantity: 1,
            personalizationValue:
              personalizationValue != null ? personalizationValue.trim() : null,
            redirectSource,
            requiredFields: formState,
            legacyRequiredFields: formState,
            productOptionKey: selectedProductOptionKey,
            productOptions: selectedProductOptions,
            selectedCropOptions: showCropOptions
              ? cropSelections.filter(Boolean)
              : [],
          }),
        );
        if (isIncentiveProduct) {
          setBackgroundSelections([]);
          if (windowWidth > 991) {
            setBackgroundsPosesSelectorVisible(true);
            // togglePosesBackgroundsSelector();
          }
        }
      }
    })();
  };

  const addBackgroundSelection = (background) => {
    const selectedBackground = product.triggers_free_backgrounds
      ? { ...background, price: 0 }
      : background;
    let updatedSelections = [...backgroundSelections];
    const targetIndex =
      activeConfigSlotIndex !== null
        ? activeConfigSlotIndex
        : updatedSelections.length < productBackgroundOptionsCount
        ? updatedSelections.length
        : Math.max(productBackgroundOptionsCount - 1, 0);

    updatedSelections[targetIndex] = selectedBackground;

    while (
      updatedSelections.length > 0 &&
      updatedSelections[updatedSelections.length - 1] === undefined
    ) {
      updatedSelections.pop();
    }

    setBackgroundSelections(updatedSelections);

    const next = pickNextConfigSlot({
      posesCount,
      productBackgroundOptionsCount,
      poseSelections,
      backgroundSelections: updatedSelections,
      lastFilled: { slotIndex: targetIndex, step: 'background' },
      skipBackgroundSelection,
      backgroundOptions,
    });

    if (next) {
      setActiveConfigSlotIndex(next.slotIndex);
      setActiveConfigStep(next.step);
      return;
    }

    setActiveConfigSlotIndex(null);
    setActiveConfigStep(null);
    setBackgroundsPosesSelectorVisible(false);
  };

  const addPoseSelection = (id, cropIndex = null) => {
    let updatedSelectionIds = [...poseSelectionIds];
    let updatedSelections = [...poseSelections];
    const targetIndex =
      activeConfigSlotIndex !== null
        ? activeConfigSlotIndex
        : updatedSelectionIds.length < posesCount
        ? updatedSelectionIds.length
        : Math.max(posesCount - 1, 0);

    // Match the same logic as renderPoses in BackgroundsPosesSelector
    let availablePosesToSelect =
      posesCount > 1
        ? availablePoses
        : studentGallery && studentGallery.length > 0
        ? studentGallery
        : availablePoses;

    // allAvailablePosesForOrder takes priority if present
    if (allAvailablePosesForOrder?.length > 0) {
      availablePosesToSelect = allAvailablePosesForOrder;
    }

    const selectedPose = {
      ...availablePosesToSelect?.find((pose) => pose.id === id),
    };

    if (cropIndex !== null) {
      selectedPose.selected_crop = selectedPose.cropped_images?.[cropIndex];
    }

    updatedSelectionIds[targetIndex] = id;
    updatedSelections[targetIndex] = selectedPose;

    while (
      updatedSelectionIds.length > 0 &&
      updatedSelectionIds[updatedSelectionIds.length - 1] === undefined
    ) {
      updatedSelectionIds.pop();
      updatedSelections.pop();
    }

    setPoseSelectionIds(updatedSelectionIds);
    setPoseSelections(updatedSelections);

    const next = pickNextConfigSlot({
      posesCount,
      productBackgroundOptionsCount,
      poseSelections: updatedSelections,
      backgroundSelections,
      lastFilled: { slotIndex: targetIndex, step: 'pose' },
      skipBackgroundSelection,
      backgroundOptions,
    });

    if (next) {
      setActiveConfigSlotIndex(next.slotIndex);
      setActiveConfigStep(next.step);
      setBackgroundsPosesSelectorVisible(true);
      return;
    }

    setActiveConfigSlotIndex(null);
    setActiveConfigStep(null);
    setBackgroundsPosesSelectorVisible(false);
  };

  const initialStepsComplete = () => {
    // Product options are optional - removed validation

    // When crop options are active, require all pose slots to have a selection
    if (showCropOptions && cropSelections.filter(Boolean).length < posesCount) {
      return false;
    }

    // In prepay mode, digital downloads that skip background selection are always ready
    // (they use all backgrounds or no background, so no selection needed)
    if (isPrepay && skipBackgroundSelection) {
      return true;
    }

    // In prepay mode, other products are pre-configured (except green screen which needs background selection)
    if (isPrepay && !isGreenScreen) {
      return true;
    }

    // For digital downloads that skip background selection, check if poses are still required
    if (skipBackgroundSelection) {
      // If there are poses available and a pose count specified, require pose selection
      // Check availablePoses, studentGallery (for PicsGallery), and allAvailablePosesForOrder
      if (
        posesCount > 0 &&
        (availablePoses?.length > 0 ||
          studentGallery?.length > 0 ||
          allAvailablePosesForOrder?.length > 0)
      ) {
        return poseSelections.length === posesCount;
      }
      // Otherwise, no selection needed
      return true;
    }

    if (isGeskus && !isPrepay && !isGreenScreen && posesCount > 0) {
      // TODO: revsit
      // special case for: 1) Geskus 2) post-event 3) non-green screen
      return true;
    }

    // TODO: revsit
    // handle case for 1) Geskus 2) pose-event 3) green screen
    if (
      isGeskus &&
      !isPrepay &&
      isGreenScreen &&
      productBackgroundOptionsCount === backgroundSelections.length
    ) {
      return true;
    }

    if (backgroundOptions.length === 0 && posesCount === 0) {
      return true;
    }

    if (posesCount > 0 && poseSelections?.length === 0 && !isPrepay) {
      return false;
    }

    if (
      !isGreenScreen &&
      !isPrepay &&
      !isGeskus &&
      posesCount > 0 &&
      posesCount === poseSelections.length
    ) {
      return true;
    }

    // TODO: revisit this
    const productBackgroundOptionsCountInt = parseInt(
      productBackgroundOptionsCount,
      10,
    );

    if (productBackgroundOptionsCountInt > 0) {
      if (productBackgroundOptionsCountInt === backgroundSelections.length) {
        return true;
      } else {
        return false;
      }
    }

    return true;
  };

  const personalizationComplete =
    !shouldOfferPersonalization ||
    personalizationOffered ||
    !(personalizationOptional || personalizationRequired);
  const canBeSubmitted = initialStepsComplete() && personalizationComplete;

  const onSkipPersonalization = () => {
    setPersonalizationValue(null);
    setPersonalizationOffered(true);
  };

  const onSubmitPersonalization = (trimmedValue) => {
    if (personalizationRequired && !trimmedValue) {
      return; // Don't hide the form with an empty required value
    }
    setPersonalizationValue(trimmedValue);
    setPersonalizationOffered(true);
  };

  const handleProductOptionChange = (optionSetKey, optionValue) => {
    setSelectedProductOptions((prev) => ({
      ...prev,
      [optionSetKey]: optionValue,
    }));
    // For legacy support, also set the single option key
    setSelectedProductOptionKey(optionValue);
  };

  const StudentGroupImageComponent = ({ isGroupImage, studentGroupImage }) => {
    if (!isGroupImage || !studentGroupImage?.url) {
      return null;
    }

    const studentName = hasStudent
      ? `${studentFirstName} ${studentLastName}`
      : `Your student`;

    return (
      <ImageContainer>
        <h6>{studentName}'s group photo:</h6>
        <ResponsiveImage src={studentGroupImage?.url} alt="Student Group" />
      </ImageContainer>
    );
  };

  // console.log(
  //   'BackgroundsPosesSelectorVisible:',
  //   backgroundsPosesSelectorVisible,
  // );

  // console.log('==========================================');
  // console.log('PERSONALIZATION SETTINGS');
  // console.log(`shouldOfferPersonalization: ${shouldOfferPersonalization}`);
  // console.log(`personalizationOffered: ${personalizationOffered}`);
  // console.log(`personalizationOptional: ${personalizationOptional}`);
  // console.log(`personalizationRequired: ${personalizationRequired}`);
  // console.log('==========================================');

  if (shippingRestricted && !isProductAllowed(product, true)) {
    return <Redirect to="/v3/offers" />;
  }

  // TODO: clean up the conditional rendering logic
  return (
    <div id="v3-regular-product-config">
      <div className="bnl-product-config">
        <BackLinkFromProduct
          hideBackLink={hideBackLink}
          hidden={isIncentiveProduct || isInterstitialProduct}
        />
        <ProductHeader product={product} />
        <ProductSubheader product={product} />
        <div className="row">
          <div className="bnl-product-config__product-info col-xs-12 col-md-4 col-lg-4">
            <div
              className={`bnl-product-config__product ${
                backgroundsPosesSelectorVisible
                  ? 'element-hidden'
                  : 'element-visible'
              }`}
            >
              {/* <div className="product-config__header">
                <h5 className="bnl-product-config__desc">{product.name}</h5>
              </div> */}
              <ZoomImageContainer>
                <ControlledZoom
                  zoomImg={{ src: product.image_large }}
                  isZoomed={isZoomed}
                  onZoomChange={handleZoomChange}
                >
                  <img
                    className="bnl-product-config__product-image"
                    src={product.image_thumbnail}
                  />
                </ControlledZoom>
                <ZoomButtonWrapper onClick={handleZoomClick}>
                  <MagnifyingGlassPlusIcon />
                </ZoomButtonWrapper>
              </ZoomImageContainer>
              <div className="bnl-product-config__product-desc">
                {product.description}
              </div>
              <StudentGroupImageComponent
                isGroupImage={isGroupImage}
                studentGroupImage={studentGroupImage}
              />
              {(backgroundOptions.length > 0 || posesCount) &&
              !skipBackgroundSelection ? (
                <SelectedPosesAndBackgrounds
                  isGeskus={isGeskus}
                  picsGalleryId={picsGalleryId}
                  backgroundOptions={backgroundOptions}
                  backgroundOptionsCount={backgroundOptionsCount}
                  backgroundSelections={backgroundSelections}
                  isGreenScreen={isGreenScreen}
                  posesCount={posesCount}
                  poseSelections={poseSelections}
                  productBackgroundOptionsCount={productBackgroundOptionsCount}
                  togglePosesBackgroundsSelector={
                    togglePosesBackgroundsSelector
                  }
                  onEditSelection={openPosesBackgroundsSelector}
                  studentPreviewImageUrl={studentPreviewImageUrl}
                  isPrepay={isPrepay}
                />
              ) : null}
              {/* {this.props.renderDesktopSelectedPoseAndBackground()} */}
              <div className="hidden-xs hidden-sm">
                <AddToCartButton
                  addProductToCart={addProductToCart}
                  canBeSubmitted={canBeSubmitted}
                  product={product}
                />
                {isInterstitialProduct && (
                  <DeclineInterstitialProductuButton product={product} />
                )}
                {isPreCartOfferProduct && (
                  <DeclinePreCartOfferProductuButton
                    product={product}
                    redirectSource={redirectSource}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="bnl-product-config__step col-xs-12 col-md-8 col-lg-8">
            {backgroundsPosesSelectorVisible && (
              <BackgroundsPosesSelector
                addBackgroundSelection={addBackgroundSelection}
                addPoseSelection={addPoseSelection}
                allAvailablePosesForOrder={allAvailablePosesForOrder}
                availablePoses={availablePoses}
                backgroundSelections={backgroundSelections}
                backgroundOptions={backgroundOptions}
                backgroundsPosesSelectorVisible={
                  backgroundsPosesSelectorVisible
                }
                activeConfigSlotIndex={activeConfigSlotIndex}
                activeConfigStep={activeConfigStep}
                isPrepay={isPrepay}
                orderContainsSingleBackgroundUpgrade={
                  orderContainsSingleBackgroundUpgrade
                }
                posesCount={posesCount}
                poseSelections={poseSelections}
                poseSelectionIds={poseSelectionIds}
                product={product}
                productBackgroundOptionsCount={productBackgroundOptionsCount}
                studentGallery={studentGallery}
                skipBackgroundSelection={skipBackgroundSelection}
              />
            )}
            {initialStepsComplete() &&
              shouldOfferPersonalization &&
              !personalizationOffered &&
              (personalizationOptional || personalizationRequired) && (
                <PersonalizationForm
                  onSkipPersonalization={onSkipPersonalization}
                  onSubmitPersonalization={onSubmitPersonalization}
                  personalizationValue={personalizationValue}
                  setPersonalizationValue={setPersonalizationValue}
                  maxLength={personalizationMaxLength}
                  personalizationRequired={personalizationRequired}
                />
              )}
            {initialStepsComplete() &&
              (!(personalizationOptional || personalizationRequired) ||
                ((personalizationOptional || personalizationRequired) &&
                  personalizationOffered) ||
                !shouldOfferPersonalization) &&
              productRequiresSMS && (
                <SMSNumberConfirmation
                  currentUserId={currentUserId}
                  editMode={editMode}
                  setEditMode={setEditMode}
                  smsNumber={smsNumber}
                  phone={phone}
                  smsNumberConfirmed={smsNumberConfirmed}
                />
              )}
            {initialStepsComplete() &&
              (!(personalizationOptional || personalizationRequired) ||
                ((personalizationOptional || personalizationRequired) &&
                  personalizationOffered) ||
                !shouldOfferPersonalization) &&
              !backgroundsPosesSelectorVisible &&
              !productRequiresSMS && (
                <DoneWrapper>
                  Configuration complete. Please click 'Add to Cart'
                </DoneWrapper>
              )}
            <RequiredProductForm
              requiredFields={product.required_product_fields}
              register={register}
              control={control}
              setValue={setValue}
            />
            <LegacyRequiredProductForm
              product={product}
              register={register}
              control={control}
            />
            {showCropOptions && (
              <CropOptionsPrepaySelector
                cropOptions={cropOptions}
                posesCount={posesCount}
                cropSelections={cropSelections}
                onCropSelectionsChange={setCropSelections}
              />
            )}
            <ProductOptionsSelector
              product={product}
              selectedOptions={selectedProductOptions}
              onOptionChange={handleProductOptionChange}
            />
            <div className="hidden-md hidden-lg hidden-xl">
              <AddToCartButton
                addProductToCart={addProductToCart}
                canBeSubmitted={canBeSubmitted}
                product={product}
              />
              {isInterstitialProduct && (
                <DeclineInterstitialProductuButton product={product} />
              )}
              {isPreCartOfferProduct && (
                <DeclinePreCartOfferProductuButton product={product} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegularProductConfig;
